import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "../components/AuthLayout";
import { Helper, TextField } from "../components/ui";
import { AuthSubmit } from "../components/AuthSubmit";
import { PasswordFields } from "../components/PasswordFields";
import { useAuth } from "../AuthContext";
import { api, ApiError } from "../api";
import { humanizeAuthError } from "../authErrors";
import { PASSWORD_MIN, passwordsReady } from "../password";
import { offerToSavePassword } from "../credentials";
import { useI18n } from "../i18n";
import { useAuthNav } from "../authTransition";
import { useFormError } from "../useFormError";
import "./auth.css";

export default function PasswordPage() {
  const nav = useNavigate();
  const { go } = useAuthNav();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pendingEmail, signIn, applyAuthResponse } = useAuth();
  const { t } = useI18n();

  // URL ?token=... → reset-by-recovery-link mode. Otherwise → regular sign-in.
  const resetToken = useMemo(() => searchParams.get("token"), [searchParams]);
  const isResetMode = Boolean(resetToken);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const { error, setError, formRef } = useFormError();

  useEffect(() => {
    if (isResetMode) return;
    if (!pendingEmail) nav("/", { replace: true });
  }, [isResetMode, pendingEmail, nav]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);

    if (!password) {
      setError(t("auth.passwordRequired"));
      return;
    }

    if (isResetMode && resetToken) {
      if (password.length < PASSWORD_MIN) {
        setError(t("auth.passwordWeak"));
        return;
      }
      if (confirm !== password) {
        setError(t("auth.passwordsMismatch"));
        return;
      }
      setBusy(true);
      try {
        const resp = await api.completePasswordRecovery({
          token: resetToken,
          new_password: password,
        });
        void offerToSavePassword(resp.user.email, password);
        applyAuthResponse(resp);
        setSearchParams({}, { replace: true });
        nav("/overview", { replace: true });
      } catch (err) {
        setError(humanizeResetError(err, t));
      } finally {
        setBusy(false);
      }
      return;
    }

    if (!pendingEmail) return;
    setBusy(true);
    try {
      await signIn(pendingEmail, password);
      nav("/overview");
    } catch (err) {
      setError(humanizeAuthError(err, t, "auth.loginFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={isResetMode ? t("auth.resetTitle") : t("auth.passwordTitle")} showBack>
      <form ref={formRef} className="auth__form anim-stagger" onSubmit={onSubmit} noValidate>
        {isResetMode ? (
          <>
            <Helper>{t("auth.resetIntro")}</Helper>
            <PasswordFields
              password={password}
              confirm={confirm}
              onPasswordChange={setPassword}
              onConfirmChange={setConfirm}
              placeholder={t("auth.newPasswordPlaceholder")}
              focused
              autoFocus
              disabled={busy}
            />
          </>
        ) : (
          <>
            {/* The email was entered on the previous step; password managers
                need it in this form to know whose password this is. */}
            <input
              type="email"
              name="email"
              autoComplete="username"
              value={pendingEmail ?? ""}
              readOnly
              hidden
            />
            <TextField
              placeholder={t("auth.passwordPlaceholder")}
              focused
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              disabled={busy}
            />
          </>
        )}
        {error ? <p className="auth-error">{error}</p> : null}
        <AuthSubmit
          busy={busy}
          ready={isResetMode ? passwordsReady(password, confirm) : undefined}
        >
          {busy
            ? isResetMode
              ? t("auth.resetSaving")
              : t("auth.signingIn")
            : isResetMode
              ? t("auth.resetSubmit")
              : t("auth.enter")}
        </AuthSubmit>
        {!isResetMode ? (
          <p className="auth-forgot">
            <button type="button" onClick={() => go("/forgot")}>
              {t("auth.forgotPassword")}
            </button>
          </p>
        ) : null}
      </form>
    </AuthLayout>
  );
}

function humanizeResetError(
  err: unknown,
  t: (k: import("../i18n").TranslationKey, vars?: Record<string, string | number>) => string,
): string {
  if (err instanceof ApiError) {
    const lower = err.message.toLowerCase();
    if (
      lower.includes("invalid") ||
      lower.includes("expired") ||
      lower.includes("not found") ||
      err.status === 400 ||
      err.status === 404 ||
      err.status === 410
    ) {
      return t("auth.resetTokenInvalid");
    }
  }
  return err instanceof Error ? err.message : t("auth.resetFailed");
}
